import { Component, signal, inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { APP_VERSION, APP_VERSION_FULL, APP_BUILD_ID, APP_RELEASE_DATE } from '../../version';

/**
 * A small fixed bottom-right build badge that opens a dialog showing the app
 * version + release date, and warns when the loaded build is stale.
 *
 * Staleness: the running bundle bakes in its own APP_BUILD_ID. On open we fetch
 * the freshly-deployed /assets/version.json (cache-busted). If the server's
 * buildId differs from ours, this tab is running an old cached bundle → we show
 * hard-refresh instructions so the user can pull the latest.
 */
interface VersionManifest {
  version: string;
  versionFull: string;
  buildId: string;
  releaseDate: string;
  angularMajor: number;
}

@Component({
  selector: 'app-version-badge',
  imports: [DatePipe, TranslateModule],
  template: `
    <button
      type="button"
      class="fixed bottom-1 right-2 z-[9998] select-none rounded px-1.5 py-0.5
             text-[11px] leading-none font-mono tracking-tight
             text-gray-500 dark:text-gray-400 bg-white/70 dark:bg-gray-800/70
             border border-gray-200 dark:border-gray-700 shadow-sm
             hover:text-gray-800 dark:hover:text-gray-100 hover:opacity-100
             focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600
             transition-colors"
      [class.!text-amber-600]="isStale()"
      [class.dark:!text-amber-400]="isStale()"
      [attr.aria-label]="'version.showDetails' | translate"
      [title]="appVersionFull"
      (click)="open()"
    >
      {{ appVersion }}@if (isStale()) {&nbsp;⟳}
    </button>

    @if (dialogOpen()) {
      <div
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
        (click)="close()"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="'version.title' | translate"
      >
        <div
          class="w-full max-w-sm rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-5"
          (click)="$event.stopPropagation()"
        >
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ 'version.title' | translate }}</h2>
            <button
              type="button"
              class="p-1 rounded text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
              [attr.aria-label]="'common.close' | translate"
              (click)="close()"
            >✕</button>
          </div>

          <dl class="text-sm space-y-2">
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'version.version' | translate }}</dt>
              <dd class="font-mono text-gray-900 dark:text-gray-100">{{ appVersion }}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'version.build' | translate }}</dt>
              <dd class="font-mono text-gray-900 dark:text-gray-100">{{ appBuildId }}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'version.released' | translate }}</dt>
              <dd class="text-gray-900 dark:text-gray-100">{{ releaseDate | date:'medium' }}</dd>
            </div>
          </dl>

          @if (isStale()) {
            <div class="mt-4 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 p-3">
              <p class="text-sm font-medium text-amber-800 dark:text-amber-300">{{ 'version.updateAvailable' | translate }}</p>
              @if (latest(); as l) {
                <p class="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  {{ 'version.latestIs' | translate }} <span class="font-mono">{{ l.version }}</span>
                  @if (l.releaseDate) { ({{ l.releaseDate | date:'medium' }}) }
                </p>
              }
              <p class="text-xs text-amber-700 dark:text-amber-400 mt-2">{{ 'version.hardRefreshHow' | translate }}</p>
              <ul class="text-xs text-amber-700 dark:text-amber-400 mt-1 list-disc list-inside space-y-0.5">
                <li>{{ 'version.hardRefreshWin' | translate }}</li>
                <li>{{ 'version.hardRefreshMac' | translate }}</li>
              </ul>
              <button
                type="button"
                class="mt-3 w-full px-3 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md
                       focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-600"
                (click)="reloadNow()"
              >{{ 'version.reloadNow' | translate }}</button>
            </div>
          } @else if (checked()) {
            <p class="mt-4 text-xs text-green-600 dark:text-green-400">{{ 'version.upToDate' | translate }}</p>
          }
        </div>
      </div>
    }
  `,
  styles: [],
})
export class VersionBadgeComponent implements OnInit {
  private readonly http = inject(HttpClient);

  readonly appVersion = APP_VERSION;
  readonly appVersionFull = APP_VERSION_FULL;
  readonly appBuildId = APP_BUILD_ID;
  readonly releaseDate = APP_RELEASE_DATE;

  readonly dialogOpen = signal(false);
  readonly isStale = signal(false);
  readonly checked = signal(false);
  readonly latest = signal<VersionManifest | null>(null);

  ngOnInit(): void {
    // Passive staleness check at startup so the badge can flag an update even
    // before the user opens the dialog. Best-effort; ignores network errors.
    this.checkLatest();
  }

  open(): void {
    this.dialogOpen.set(true);
    this.checkLatest(); // re-check on open in case a deploy happened since load
  }

  close(): void {
    this.dialogOpen.set(false);
  }

  reloadNow(): void {
    // Bypass the HTTP cache for the navigation so the freshest index.html/bundles load.
    location.reload();
  }

  private checkLatest(): void {
    // Cache-bust so we read the CURRENTLY deployed manifest, not a cached copy.
    this.http.get<VersionManifest>(`/assets/version.json?ts=${Date.now()}`).subscribe({
      next: (m) => {
        this.latest.set(m);
        this.checked.set(true);
        this.isStale.set(!!m?.buildId && m.buildId !== this.appBuildId);
      },
      error: () => {
        // Can't reach the manifest — don't claim staleness.
        this.checked.set(true);
      },
    });
  }
}
